// (h) BinReveal — card flips (≤300ms) to reveal the bin, marks, and a message.
import { motion } from 'framer-motion';
import type { BinColor } from '@/types';
import { BIN_BLURB, BIN_LABEL, binRamp } from '@/lib/bins';

interface BinRevealProps {
  bin: BinColor;
  earnedMarks: number;
  maxMarks: number;
  overTime?: boolean;
}

export function BinReveal({ bin, earnedMarks, maxMarks, overTime }: BinRevealProps) {
  // `bg` + `deep`, not the saturated dot tone + white. White on the dot tone was
  // 2.2–3.0:1 in dark and already failed three bins in light; `fill` + `deep`
  // fixes light but still leaves dark Green at 4.40:1. This pairing is the only
  // one that clears AA across all five bins in both themes (worst case 12:1),
  // because the two stops invert together. The colour now carries on the border
  // and the large type rather than a slab of fill.
  const ramp = binRamp(bin);

  return (
    <div className="flex justify-center py-4" style={{ perspective: 1000 }}>
      <motion.div
        className="relative w-full max-w-xs rounded-2xl border-2 p-6 text-center shadow-lg"
        style={{
          backgroundColor: ramp.bg,
          borderColor: ramp.border,
          color: ramp.deep,
          transformStyle: 'preserve-3d',
        }}
        initial={{ rotateY: 90, opacity: 0 }}
        animate={{ rotateY: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        role="status"
        aria-live="polite"
      >
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.18 }}
        >
          <p className="text-sm font-medium opacity-90">You moved this to</p>
          <p className="my-1 text-3xl font-extrabold tracking-tight">{BIN_LABEL[bin]}!</p>
          <p className="text-lg font-semibold">
            {earnedMarks} / {maxMarks} marks
          </p>
          {overTime && <p className="mt-1 text-xs opacity-90">Correct — a little over time.</p>}
          <p className="mt-3 text-sm opacity-95">{BIN_BLURB[bin]}</p>
        </motion.div>
      </motion.div>
    </div>
  );
}
